// useCostTableRows.ts (or costTableRows.ts, etc.)
import DecimalTextField from '../ConfirmationPage/NDCServiceFee';
import React from 'react';
import { Box } from '@mui/material';
import * as TFF from '@tff/types/TFF';
import { IntlShape } from 'react-intl';
import CostBondRow from '../ConfirmationPage/CostBondRow';
import CostChargeRow from '../ConfirmationPage/CostChargeRow';

interface TableRowData {
  label: string;
  originalBooking: string | React.ReactNode;
  newBooking: string | React.ReactNode;
  surcharge: string | React.ReactNode;
  rowStyle?: React.CSSProperties;
  cellStyle?: {
    label?: React.CSSProperties;
    originalBooking?: React.CSSProperties;
    newBooking?: React.CSSProperties;
    surcharge?: React.CSSProperties;
  };
}

interface CreateRowsArgs {
  originalJourneys: TFF.Journey[];
  newBookingJourneys: TFF.Journey[];
  isRoundTrip: boolean;
  totalAmount: number;
  locale: { locale: string };
  serviceFee: string;
  setServiceFee: React.Dispatch<React.SetStateAction<string>>;
  taxAmount: number;
  penalty: number;
  baseAmount: number;
  intl: IntlShape;
  numberFormatter: Intl.NumberFormat;
}

export const createCostTableRows = ({
  originalJourneys,
  newBookingJourneys,
  isRoundTrip,
  totalAmount,
  locale,
  serviceFee,
  setServiceFee,
  taxAmount,
  penalty,
  baseAmount,
  intl,
  numberFormatter,
}: CreateRowsArgs): TableRowData[] => {
  const originalOutboundJourney = originalJourneys[0];
  const originalInboundJourney = originalJourneys[originalJourneys.length - 1];
  const newOutboundJourney = newBookingJourneys[0];
  const newInboundJourney = newBookingJourneys[newBookingJourneys.length - 1];

  const commonBondCellStyles = {
    originalBooking: {
      backgroundColor: '#F5F5F5',
      borderColor: '#BABABA',
      whiteSpace: 'pre-line',
    },
    newBooking: {
      backgroundColor: '#E2F5FD',
      borderColor: '#BABABA',
      whiteSpace: 'pre-line',
    },
    surcharge: {
      borderColor: '#BABABA',
    },
  };

  const commonChargesCellStyles = (isSum?: boolean) => {
    return {
      label: {
        border: 'none',
      },
      originalBooking: {
        backgroundColor: '#F5F5F5',
        borderColor: '#BABABA',
        fontWeight: isSum ? 'bold' : 'regular',
      },
      newBooking: {
        backgroundColor: '#E2F5FD',
        borderColor: '#BABABA',
        fontWeight: isSum ? 'bold' : 'regular',
      },
      surcharge: {
        borderColor: '#BABABA',
        fontWeight: isSum ? 'bold' : 'regular',
      },
    };
  };

  const generateOutboundRow = () => {
    return {
      label: `${intl.formatMessage({ id: 'rebook.directionOutbound' }).toUpperCase()}`,
      originalBooking: <CostBondRow journey={originalOutboundJourney} locale={locale} />,
      newBooking: <CostBondRow journey={newOutboundJourney} locale={locale} />,
      surcharge: '',
      cellStyle: {
        ...commonBondCellStyles,
        label: {
          borderColor: '#BABABA',
        },
      },
    };
  };

  const generateInboundRow = () => {
    return {
      label: `${intl.formatMessage({ id: 'rebook.directionInbound' }).toUpperCase()}`,
      originalBooking: <CostBondRow journey={originalInboundJourney} locale={locale} />,
      newBooking: <CostBondRow journey={newInboundJourney} locale={locale} />,
      surcharge: '',
      cellStyle: {
        ...commonBondCellStyles,
      },
    };
  };

  const generateChargeRow = (textId: string, amount: number) => {
    return {
      label: '',
      originalBooking: '',
      newBooking: <CostChargeRow textId={textId} amount={amount} />,
      surcharge: <CostChargeRow textId={''} amount={amount} />,
      cellStyle: commonChargesCellStyles(),
    };
  };

  const generateServiceFeeRow = () => {
    return {
      label: '',
      originalBooking: '',
      newBooking: (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={-1} mt={-1}>
          <span>{intl.formatMessage({ id: 'ndc.rebookServiceFee' }).toUpperCase()}</span>
          <DecimalTextField setServiceFee={setServiceFee} serviceFee={serviceFee} />
        </Box>
      ),
      surcharge: '',
      cellStyle: commonChargesCellStyles(),
    };
  };

  const generateSumRow = () => {
    const zeroValue = numberFormatter.format(0);
    const decimalSeparator = zeroValue.includes(',') ? ',' : '.';
    const serviceFeeNumber = Number(serviceFee.replace(decimalSeparator, '.'));

    return {
      label: '',
      originalBooking: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{intl.formatMessage({ id: 'ndc.rebookSumCancelled' }).toUpperCase()}</span>
          <span>{zeroValue}</span>
        </div>
      ),
      newBooking: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{intl.formatMessage({ id: 'ndc.rebookSumNew' }).toUpperCase()}</span>
          <span>{numberFormatter.format(Number(totalAmount < 0 ? 0 : totalAmount) + serviceFeeNumber)}</span>
        </div>
      ),
      surcharge: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{intl.formatMessage({ id: 'ndc.rebookTotalToPay' }).toUpperCase()}</span>
          <span>{numberFormatter.format(Number(totalAmount < 0 ? 0 : totalAmount) + serviceFeeNumber)}</span>
        </div>
      ),
      cellStyle: commonChargesCellStyles(true),
    };
  };

  const costRows = [
    generateOutboundRow(),
    isRoundTrip && generateInboundRow(),
    generateChargeRow('ndc.rebookFareDifference', baseAmount),
    generateChargeRow('ndc.rebookTaxDifference', taxAmount),
    generateChargeRow('ndc.rebookPenalty', penalty),
    generateServiceFeeRow(),
    generateSumRow(),
  ];

  return costRows as TableRowData[];
};
